import React, { PropsWithChildren, useState } from 'react';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import Button from 'components/atoms/Button';
import { useThemeBreakpoint } from 'hooks/useThemeBreakpoint';
import { Fade, Grid, Modal, Typography } from '@mui/material';
import { EIconTypes } from 'constants/Icons';
import { colors } from 'theme/colors';
import { useTranslations } from 'hooks/useTranslations';
import { CustomModalRoot, ModalContent } from './styles';

interface IFormModal extends PropsWithChildren {
  onClickApprove?: () => void;
  onClickClose: () => void;
  open: boolean;
  title?: string;
  text?: string;
  approveButtonLabel?: string;
  approveButtonIcon?: EIconTypes;
  approveButtonStyle?: 'success' | 'error';
  approveButtonSubmit?: boolean;
  onSubmit?: () => void;
  cancelButtonLabel?: string;
  minWidth?: string;
  maxWidth?: string;
  width?: string;
  titleLeft?: boolean;
  titleColor?: string;
  disableBackdropClick?: boolean;
}

interface IModalForm extends PropsWithChildren {
  isForm?: boolean;
  onSubmit?: () => void;
}

const ModalForm = ({ children, isForm, onSubmit }: IModalForm) => {
  if (!isForm) return <>{children}</>;
  return <form onSubmit={onSubmit}>{children}</form>;
};

export const FormModal = ({
  onClickApprove,
  onClickClose,
  open,
  title,
  text,
  approveButtonLabel,
  approveButtonIcon,
  approveButtonStyle,
  approveButtonSubmit = false,
  onSubmit,
  cancelButtonLabel,
  children,
  minWidth,
  maxWidth,
  width,
  titleLeft = false,
  titleColor,
  disableBackdropClick = false,
}: IFormModal) => {
  const { t } = useTranslations();
  const { isDesktop } = useThemeBreakpoint();
  const [isLoading, setIsLoading] = useState(false);

  const handleApprove = async () => {
    if (onClickApprove) {
      setIsLoading(true);
      try {
        await onClickApprove();
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <CustomModalRoot style={{ width: open ? '100%' : '0', height: open ? '100%' : '0' }}>
      <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        open={open}
        onClose={(event, reason) => {
          if (disableBackdropClick && reason === 'backdropClick') return;
          onClickClose();
        }}
        closeAfterTransition
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          '& .MuiBackdrop-root': {
            backgroundColor: colors.modalBackdrop,
          },
        }}
      >
        <Fade in={open}>
          <ModalContent
            style={{
              width: isDesktop ? width || '50%' : '100%',
              minWidth: isDesktop ? minWidth || '780px' : 'auto',
              maxWidth: maxWidth || '780px',
            }}
          >
            {title && (
              <Typography
                variant="h5"
                sx={
                  titleLeft
                    ? {
                        color: titleColor ? titleColor : colors.primary500,
                        textAlign: 'left',
                        margin: 0,
                      }
                    : { textAlign: 'center' }
                }
              >
                {title}
              </Typography>
            )}
            {text && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                {text}
              </Typography>
            )}
            {children}
            <ModalForm isForm={approveButtonSubmit} onSubmit={onSubmit}>
              <Grid
                container
                columnSpacing={2}
                sx={{ rowGap: 2, marginTop: '12px', justifyContent: 'right' }}
              >
                <Grid
                  size={{ xs: 12, sm: onClickApprove || approveButtonSubmit ? 6 : 12, md: 4 }}
                >
                  <Button
                    fullWidth
                    label={t(cancelButtonLabel || 'general.cancel.button')}
                    id="legal_entity-cancel"
                    variant={EButtonVariants.outlined}
                    onClick={onClickClose}
                    size={EButtonSizes.small}
                    disabled={isLoading}
                  />
                </Grid>
                {onClickApprove && (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Button
                      fullWidth
                      label={t(approveButtonLabel || 'general.save.button')}
                      icon={approveButtonIcon || undefined}
                      styleVariant={approveButtonStyle || undefined}
                      id="legal_entity-save"
                      variant={EButtonVariants.contained}
                      onClick={handleApprove}
                      isLoading={isLoading}
                      size={EButtonSizes.small}
                    />
                  </Grid>
                )}
                {approveButtonSubmit && (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Button
                      fullWidth
                      type="submit"
                      label={t(approveButtonLabel || 'general.save.button')}
                      icon={approveButtonIcon || undefined}
                      styleVariant={approveButtonStyle || undefined}
                      id="legal_entity-save"
                      variant={EButtonVariants.contained}
                      isLoading={isLoading}
                      size={EButtonSizes.small}
                    />
                  </Grid>
                )}
              </Grid>
            </ModalForm>
          </ModalContent>
        </Fade>
      </Modal>
    </CustomModalRoot>
  );
};
